import {
  CommissionDetail,
  commissionAmountMax,
  commissionDetailIsComplete,
  shouldOmitAmount,
} from "@platform/formatting-utils";
import { COVERAGE_FORM, ERROR_MESSAGES } from "@platform/rfp-core";

const {
  CURRENT_COMMISSION,
  CURRENT_COMMISSION_DETAIL,
  REQUESTED_COMMISSION,
  REQUESTED_COMMISSION_DETAIL,
} = COVERAGE_FORM;

const {
  REQUIRED_FIELD,
  COMMISSION_AMOUNT_REQUIRED,
  COMMISSION_PERCENTAGE_REQUIRED,
  COMMISSION_PEPM_MAX,
  COMMISSION_HEAPED_PAIR_REQUIRED,
} = ERROR_MESSAGES;

export type GradedMode = "none" | "percent";

export interface HeapedDraft {
  first?: number;
  thereafter?: number;
}

export interface FormCommissionDetail extends CommissionDetail {
  heapedDraft?: HeapedDraft;
  gradedMode?: GradedMode;
}

const isHalfHeaped = (draft?: HeapedDraft): boolean =>
  (typeof draft?.first === "number") !==
  (typeof draft?.thereafter === "number");

const isPercentGradedWithoutAmount = (detail: FormCommissionDetail): boolean =>
  detail.gradedMode === "percent" && typeof detail.amount !== "number";

/** What the type is missing, in the terms its own input is labelled with. */
const incompleteMessage = (type: string): string =>
  type === "percentage"
    ? COMMISSION_PERCENTAGE_REQUIRED
    : COMMISSION_AMOUNT_REQUIRED;

/** A plan's form values. Only the commission keys are read here. */
type PlanValues = Record<string, unknown> | undefined;

/**
 * The error string for a field, or false when it is valid — the shape the
 * coverage validators expect.
 */
type ValidationResult = string | false;

const detailAt = (
  plan: PlanValues,
  key: string,
): FormCommissionDetail | undefined =>
  plan?.[key] as FormCommissionDetail | undefined;

const freeTextAt = (plan: PlanValues, key: string): string | undefined =>
  plan?.[key] as string | undefined;

/**
 * Validates a normalized commission detail. The type is what a required
 * commission needs; percentage and PEPM also demand their number, graded
 * demands Highest % under "Percent graded", and heaped takes both of its
 * percentages or neither.
 */
export const validateCommissionDetail = (
  detail: FormCommissionDetail | undefined,
  isRequired: boolean,
): ValidationResult => {
  const type = detail?.type;

  if (!type) {
    return isRequired ? REQUIRED_FIELD : false;
  }

  if (shouldOmitAmount(type)) {
    return false;
  }

  if (type === "heaped" && isHalfHeaped(detail?.heapedDraft)) {
    return COMMISSION_HEAPED_PAIR_REQUIRED;
  }

  if (type === "graded" && detail && isPercentGradedWithoutAmount(detail)) {
    return COMMISSION_PERCENTAGE_REQUIRED;
  }

  if (!commissionDetailIsComplete(detail)) {
    return incompleteMessage(type);
  }

  const { amount } = detail ?? {};

  const max = commissionAmountMax(type);
  if (typeof amount === "number" && max !== undefined && amount > max) {
    return type === "pepm" ? COMMISSION_PEPM_MAX : REQUIRED_FIELD;
  }

  return false;
};

/**
 * Validates whichever commission shape a plan carries.
 *
 * The normalized detail replaces the free text once its key is present. Until
 * then the original string check governs, so a coverage captured before
 * commissions were normalized keeps validating the way it always did — which
 * is what keeps this free of any feature-flag check.
 *
 * The two shapes can disagree on whether a value is required: a requested
 * commission was mandatory as free text and is optional once normalized.
 */
const validateCommission = (
  plan: PlanValues,
  detailKey: string,
  freeTextKey: string,
  required: { asFreeText: boolean; asDetail: boolean },
): ValidationResult => {
  const detail = detailAt(plan, detailKey);

  if (detail === undefined) {
    return required.asFreeText && !freeTextAt(plan, freeTextKey)?.trim()
      ? REQUIRED_FIELD
      : false;
  }

  return validateCommissionDetail(detail, required.asDetail);
};

export const validateCurrentCommission = (plan: PlanValues): ValidationResult =>
  validateCommission(plan, CURRENT_COMMISSION_DETAIL, CURRENT_COMMISSION, {
    asFreeText: true,
    asDetail: true,
  });

export const validateRequestedCommission = (
  plan: PlanValues,
): ValidationResult =>
  validateCommission(plan, REQUESTED_COMMISSION_DETAIL, REQUESTED_COMMISSION, {
    asFreeText: true,
    asDetail: false,
  });

export const validateCurrentCommissionDetail = (
  plan: PlanValues,
): ValidationResult =>
  validateCommissionDetail(detailAt(plan, CURRENT_COMMISSION_DETAIL), true);

export const validateRequestedCommissionDetail = (
  plan: PlanValues,
): ValidationResult =>
  validateCommissionDetail(detailAt(plan, REQUESTED_COMMISSION_DETAIL), false);
